<template>
  <div class="labs-extrusion-detail">
    <div class="ui container">

      <div class="ui breadcrumb" style="margin: 20px 0 10px 0;">
        <router-link to="/" class="section">Home</router-link>
        <i class="right chevron icon divider"></i>
        <router-link to="/labs" class="section">Labs</router-link>
        <i class="right chevron icon divider"></i>
        <router-link to="/labs/extrusions" class="section">Extrusions</router-link>
        <i class="right chevron icon divider"></i>
        <div class="active section">{{ extrusion ? (extrusion.testNumber || 'Extrusion #' + extrusion.id) : 'Detail' }}</div>
      </div>

      <div v-if="loading" class="ui active centered inline loader"></div>

      <div v-else-if="error" class="ui negative message">
        <p>{{ error }}</p>
      </div>

      <div v-else-if="extrusion">

        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:20px;">
          <h2 class="ui header" style="margin:0;">
            <i class="industry icon"></i>
            <div class="content">
              {{ extrusion.testNumber || 'Extrusion #' + extrusion.id }}
              <div class="sub header">{{ formatDate(extrusion.extrusionDate) }}</div>
            </div>
          </h2>
          <div>
            <router-link :to="'/labs/mixes/' + extrusion.labsMixId" class="ui button" style="margin-right:8px;">
              <i class="tint icon"></i> View Mix
            </router-link>
            <router-link to="/labs/extrusions" class="ui button">
              <i class="arrow left icon"></i> Back
            </router-link>
          </div>
        </div>

        <!-- Key Metrics -->
        <div class="ui four stackable cards" style="margin-bottom:20px;">
          <div class="card">
            <div class="content">
              <div class="header">WBM %</div>
              <div class="description" style="font-size:1.4em; font-weight:bold;">
                {{ extrusion.wbm != null ? extrusion.wbm + '%' : '—' }}
              </div>
            </div>
          </div>
          <div class="card">
            <div class="content">
              <div class="header">Throughput</div>
              <div class="description" style="font-size:1.4em; font-weight:bold;">
                {{ extrusion.throughputGMin != null ? extrusion.throughputGMin + ' g/min' : '—' }}
              </div>
            </div>
          </div>
          <div class="card">
            <div class="content">
              <div class="header">Extrusion Pressure</div>
              <div class="description" style="font-size:1.4em; font-weight:bold;">
                {{ extrusion.extrusionPressureBar != null ? extrusion.extrusionPressureBar + ' bar' : '—' }}
              </div>
            </div>
          </div>
          <div class="card">
            <div class="content">
              <div class="header">Extrudability</div>
              <div class="description" style="font-size:1.4em; font-weight:bold;">
                {{ extrusion.extrudabilityRating0To10 != null ? extrusion.extrudabilityRating0To10 + ' / 10' : '—' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Die & Setup -->
        <h3 class="ui dividing header">Die & Setup</h3>
        <table class="ui definition table" style="margin-bottom:20px;">
          <tbody>
            <tr><td>Extruder Type</td><td>{{ extrusion.extruderType || '—' }}</td></tr>
            <tr><td>Die Number</td><td>{{ extrusion.dieNumber || '—' }}</td></tr>
            <tr><td>Pellet Diameter</td><td>{{ extrusion.pelletDiameterIn != null ? extrusion.pelletDiameterIn + ' in' : '—' }}</td></tr>
            <tr><td>Die Temp</td><td>{{ extrusion.dieTempC != null ? extrusion.dieTempC + ' °C' : '—' }}</td></tr>
            <tr><td>Holes Count</td><td>{{ extrusion.holesCount != null ? extrusion.holesCount : '—' }}</td></tr>
            <tr><td>Insert</td><td>{{ extrusion.insertType || '—' }}</td></tr>
            <tr><td>Step Number</td><td>{{ extrusion.stepNumber || '—' }}</td></tr>
          </tbody>
        </table>

        <!-- Auger -->
        <h3 class="ui dividing header">Auger</h3>
        <table class="ui definition table" style="margin-bottom:20px;">
          <tbody>
            <tr><td>Auger Label</td><td>{{ extrusion.augerLabel || '—' }}</td></tr>
            <tr><td>Displacement (cc/rev)</td><td>{{ extrusion.augerDisplacementCcRev != null ? extrusion.augerDisplacementCcRev : '—' }}</td></tr>
            <tr><td>Actual Displacement (cc/rev)</td><td>{{ extrusion.actualDisplacementCcRev != null ? extrusion.actualDisplacementCcRev : '—' }}</td></tr>
            <tr><td>Extruder RPM</td><td>{{ extrusion.extruderRpm != null ? extrusion.extruderRpm : '—' }}</td></tr>
            <tr><td>Extruder Torque (Nm)</td><td>{{ extrusion.extruderTorqueNm != null ? extrusion.extruderTorqueNm : '—' }}</td></tr>
            <tr><td>Motor Amps</td><td>{{ extrusion.motorAmps != null ? extrusion.motorAmps : '—' }}{{ extrusion.motorAmpComment ? ' — ' + extrusion.motorAmpComment : '' }}</td></tr>
          </tbody>
        </table>

        <!-- Process Conditions -->
        <h3 class="ui dividing header">Process Conditions</h3>
        <table class="ui definition table" style="margin-bottom:20px;">
          <tbody>
            <tr><td>Ambient Temp</td><td>{{ extrusion.ambientTempC != null ? extrusion.ambientTempC + ' °C' : '—' }}</td></tr>
            <tr><td>Product Temp</td><td>{{ extrusion.productTempC != null ? extrusion.productTempC + ' °C' : '—' }}</td></tr>
            <tr><td>Vacuum</td><td>{{ extrusion.vacuumHgMbar != null ? extrusion.vacuumHgMbar + ' ' + (extrusion.vacuumUnit || '') : '—' }}</td></tr>
            <tr><td>Water Added</td><td>{{ extrusion.waterAdded != null ? extrusion.waterAdded : '—' }}</td></tr>
            <tr><td>WBM After Shear</td><td>{{ extrusion.wbmAfterShearPct != null ? extrusion.wbmAfterShearPct + '%' : '—' }}</td></tr>
            <tr><td>Sheared Temp</td><td>{{ extrusion.shearedTempC != null ? extrusion.shearedTempC + ' °C' : '—' }}</td></tr>
            <tr><td>Density</td><td>{{ extrusion.density != null ? extrusion.density : '—' }}{{ extrusion.densityComment ? ' — ' + extrusion.densityComment : '' }}</td></tr>
            <tr><td>Penetrometer</td><td>{{ extrusion.penetrometer != null ? extrusion.penetrometer : '—' }}</td></tr>
            <tr><td>Successful</td><td>
              <span :class="extrusion.successful === 'Yes' ? 'ui green label' : extrusion.successful === 'No' ? 'ui red label' : 'ui grey label'">
                {{ extrusion.successful || '—' }}
              </span>
            </td></tr>
            <tr><td>Comments</td><td>{{ extrusion.comments || '—' }}</td></tr>
          </tbody>
        </table>

        <!-- Specimens from this extrusion -->
        <h3 class="ui dividing header">Specimens</h3>
        <div v-if="loadingSpecimens" class="ui active inline loader"></div>
        <div v-else-if="specimens.length === 0" class="ui info message">
          <p>No specimens recorded for this extrusion yet.</p>
        </div>
        <table v-else class="ui celled striped table">
          <thead>
            <tr>
              <th>Sample #</th>
              <th>Test Type</th>
              <th>Days Cured</th>
              <th>Compression (MPa)</th>
              <th>Tumble Index</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sp in specimens" :key="sp.id">
              <td>{{ sp.sampleNumber || '—' }}</td>
              <td>{{ sp.testType || '—' }}</td>
              <td>{{ sp.totalDaysCured != null ? sp.totalDaysCured : '—' }}</td>
              <td>{{ sp.axialCompressionMpa != null ? sp.axialCompressionMpa : '—' }}</td>
              <td>{{ sp.tumbleIndexIso3271 != null ? sp.tumbleIndexIso3271 : '—' }}</td>
              <td>
                <router-link to="/labs/specimens" class="ui mini primary button">View All</router-link>
              </td>
            </tr>
          </tbody>
        </table>

      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "LabsExtrusionDetail",
  data() {
    return {
      extrusion: null,
      specimens: [],
      loading: true,
      loadingSpecimens: true,
      error: null,
    };
  },
  methods: {
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleDateString();
    },
    async loadExtrusion() {
      try {
        const response = await axios.get("/api/labs/extrusions/" + this.$route.params.id);
        this.extrusion = response.data;
      } catch (err) {
        this.error = "Unable to load extrusion details.";
      } finally {
        this.loading = false;
      }
    },
    async loadSpecimens() {
      try {
        const response = await axios.get("/api/labs/specimens/by-extrusion/" + this.$route.params.id);
        this.specimens = response.data;
      } catch (err) {
        this.specimens = [];
      } finally {
        this.loadingSpecimens = false;
      }
    },
  },
  mounted() {
    this.loadExtrusion();
    this.loadSpecimens();
  },
};
</script>
